import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import ReactMarkdown from "react-markdown";
import {
  Activity, AlertTriangle, BookOpen, Brain, Download, FileText, GraduationCap,
  LineChart as LineIcon, Loader2, Sparkles, TrendingUp, Users, Wallet,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { requireRole } from "@/lib/session";
import { useSchoolContext } from "@/lib/school-context";
import { intelligenceOverview, executiveReport } from "@/lib/school-intelligence.functions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/intelligence")({
  ssr: false,
  beforeLoad: () => requireRole("admin"),
  head: () => ({ meta: [{ title: "School Intelligence · MAKAI" }] }),
  component: Page,
});

const UGX = (n: number) => new Intl.NumberFormat("en-UG").format(Math.round(n || 0));
const PIE_COLORS = ["#0F766E", "#D97706", "#7C3AED", "#DB2777", "#2563EB", "#65A30D", "#DC2626"];

function Page() {
  const { active, loading } = useSchoolContext();
  const schoolId = active?.school_id ?? null;
  return (
    <AdminShell title="School Intelligence" subtitle="AI-powered predictions and executive analytics across your school.">
      {loading ? <Loading /> : !schoolId ? <NoSchool /> : <Dashboard key={schoolId} schoolId={schoolId} />}
    </AdminShell>
  );
}

function Loading() {
  return <div className="rounded-2xl border bg-card p-10 text-center text-sm text-muted-foreground">Loading…</div>;
}
function NoSchool() {
  return <div className="rounded-2xl border bg-card p-10 text-center text-sm text-muted-foreground">Select a school to view intelligence.</div>;
}

function Dashboard({ schoolId }: { schoolId: string }) {
  const fetchOverview = useServerFn(intelligenceOverview);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["intel", schoolId],
    queryFn: () => fetchOverview({ data: { schoolId } }),
  });

  if (isLoading) return <Loading />;
  if (error || !data) return (
    <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-sm">
      <div className="font-semibold text-destructive">Could not load intelligence</div>
      <div className="mt-1 text-muted-foreground">{(error as Error)?.message ?? "Unknown error"}</div>
      <Button className="mt-3" onClick={() => refetch()} size="sm">Retry</Button>
    </div>
  );

  const k = data.kpis;
  const p = data.prediction;

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <KpiCard icon={<Users className="h-4 w-4" />} label="Active learners" value={String(k.activeLearners)} tone="primary" />
        <KpiCard icon={<TrendingUp className="h-4 w-4" />} label="Avg score" value={k.avgAssessmentScorePct != null ? `${k.avgAssessmentScorePct}%` : "—"} tone="success" />
        <KpiCard icon={<Activity className="h-4 w-4" />} label="Attendance 30d" value={k.attendancePct30d != null ? `${k.attendancePct30d}%` : "—"} tone="info" />
        <KpiCard icon={<Wallet className="h-4 w-4" />} label="Outstanding fees" value={`UGX ${UGX(k.feesOutstandingUgx)}`} tone="warn" />
        <KpiCard icon={<GraduationCap className="h-4 w-4" />} label="Active staff" value={String(k.activeStaff)} tone="default" />
      </div>

      {/* Prediction */}
      <Card title="Predicted Exam Readiness" icon={<Brain className="h-4 w-4 text-primary" />}>
        <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-center">
          <div className="flex flex-col items-center justify-center rounded-2xl bg-primary-soft p-5">
            <div className="text-4xl font-bold text-primary">{p.predictedAvgPct}%</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-primary/80">{p.band}</div>
            <div className="mt-1 text-xs text-muted-foreground">~{p.projectedPassLearners} learners projected to pass</div>
          </div>
          <div className="space-y-2">
            {p.drivers.map((d) => (
              <div key={d.factor}>
                <div className="flex items-center justify-between text-xs"><span>{d.factor}</span><span className="font-mono">{Math.round(d.value)}% · weight {d.weightPct}%</span></div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, d.value)}%` }} />
                </div>
              </div>
            ))}
            <p className="pt-1 text-xs text-muted-foreground">Predictor blends recent assessment averages (65%) with 30-day attendance (35%).</p>
          </div>
        </div>
      </Card>

      {/* Trends */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Attendance trend (weekly)" icon={<LineIcon className="h-4 w-4 text-primary" />}>
          <div className="h-60">
            <ResponsiveContainer>
              <LineChart data={data.attendance.trend}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="week" fontSize={11} />
                <YAxis fontSize={11} domain={[0, 100]} />
                <Tooltip />
                <Line type="monotone" dataKey="attendancePct" stroke="#0F766E" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Subject performance" icon={<TrendingUp className="h-4 w-4 text-primary" />}>
          <div className="h-60">
            <ResponsiveContainer>
              <BarChart data={data.subjects.slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="subject" fontSize={10} angle={-15} textAnchor="end" height={50} />
                <YAxis fontSize={11} domain={[0, 100]} />
                <Tooltip />
                <Bar dataKey="avgPct" fill="#D97706" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Teacher performance */}
      <Card title="Teacher performance" icon={<GraduationCap className="h-4 w-4 text-primary" />}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr><th className="py-2 pr-3">Teacher</th><th className="py-2 pr-3">Avg class score</th><th className="py-2 pr-3">Assessments</th><th className="py-2 pr-3">Lesson plans (30d)</th><th className="py-2">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {data.teachers.slice(0, 12).map((t) => {
                const score = t.avgClassScorePct;
                const tone = score == null ? "muted" : score >= 70 ? "success" : score >= 55 ? "warn" : "danger";
                return (
                  <tr key={t.teacherId}>
                    <td className="py-2 pr-3 font-medium">{t.name}</td>
                    <td className="py-2 pr-3 font-mono">{score != null ? `${score}%` : "—"}</td>
                    <td className="py-2 pr-3">{t.assessmentsGiven}</td>
                    <td className="py-2 pr-3">{t.lessonPlans30d}</td>
                    <td className="py-2"><Pill tone={tone}>{score == null ? "No data" : score >= 70 ? "Strong" : score >= 55 ? "Average" : "Needs support"}</Pill></td>
                  </tr>
                );
              })}
              {data.teachers.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No teacher data yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Curriculum + Library */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Curriculum coverage" icon={<BookOpen className="h-4 w-4 text-primary" />}>
          <div className="mb-3 text-sm">
            Overall: <span className="font-semibold">{data.curriculum.overallCoveragePct}%</span> ({data.curriculum.publishedLessons}/{data.curriculum.totalLessons} lessons published)
          </div>
          <div className="space-y-2">
            {data.curriculum.bySubject.slice(0, 8).map((s) => (
              <div key={s.subject}>
                <div className="flex items-center justify-between text-xs"><span>{s.subject}</span><span className="font-mono">{s.coveragePct}%</span></div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-emerald-600" style={{ width: `${Math.min(100, s.coveragePct)}%` }} />
                </div>
              </div>
            ))}
            {data.curriculum.bySubject.length === 0 && <div className="text-sm text-muted-foreground">No lessons yet.</div>}
          </div>
        </Card>
        <Card title="Library usage (30 days)" icon={<BookOpen className="h-4 w-4 text-primary" />}>
          <div className="mb-3 grid grid-cols-3 gap-2 text-center">
            <Mini label="Resources" value={String(data.library.totalResources)} />
            <Mini label="Views" value={String(data.library.views30d)} />
            <Mini label="Unique users" value={String(data.library.uniqueViewers30d)} />
          </div>
          {data.library.byKind.length > 0 && (
            <div className="h-44">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data.library.byKind} dataKey="count" nameKey="kind" outerRadius={70} label={(e) => e.kind}>
                    {data.library.byKind.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          {data.library.topResources.length > 0 && (
            <div className="mt-2">
              <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Top resources</div>
              <ul className="space-y-1 text-sm">
                {data.library.topResources.slice(0, 5).map((r, i) => (
                  <li key={i} className="flex justify-between gap-3"><span className="truncate">{r.title}</span><span className="font-mono text-xs text-muted-foreground">{r.views}</span></li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>

      {/* Finance */}
      <Card title="Finance snapshot" icon={<Wallet className="h-4 w-4 text-primary" />}>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Mini label="Billed" value={`UGX ${UGX(data.fees.billedUgx)}`} />
          <Mini label="Collected" value={`UGX ${UGX(data.fees.collectedUgx)}`} />
          <Mini label="Outstanding" value={`UGX ${UGX(data.fees.outstandingUgx)}`} />
          <Mini label="Collection rate" value={`${data.fees.collectionRatePct}%`} />
        </div>
      </Card>

      {/* At-risk */}
      <Card title="At-risk learners" icon={<AlertTriangle className="h-4 w-4 text-amber-600" />}>
        {data.atRisk.length === 0 ? (
          <div className="text-sm text-muted-foreground">No learners flagged — great work!</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr><th className="py-2 pr-3">Learner</th><th className="py-2 pr-3">Adm. no</th><th className="py-2 pr-3">Avg</th><th className="py-2 pr-3">Attendance</th><th className="py-2">Reasons</th></tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {data.atRisk.map((r, i) => (
                  <tr key={i}>
                    <td className="py-2 pr-3 font-medium">{r.name}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{r.admissionNo ?? "—"}</td>
                    <td className="py-2 pr-3 font-mono">{r.avgScorePct != null ? `${r.avgScorePct}%` : "—"}</td>
                    <td className="py-2 pr-3 font-mono">{r.attendancePct != null ? `${r.attendancePct}%` : "—"}</td>
                    <td className="py-2 text-xs text-muted-foreground">{r.reasons.join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Teachers without lesson plans */}
      {data.missingPlans.count > 0 && (
        <Card title={`Teachers without lesson plans (${data.missingPlans.windowDays}d)`} icon={<AlertTriangle className="h-4 w-4 text-amber-600" />}>
          <ul className="grid gap-2 text-sm sm:grid-cols-2">
            {data.missingPlans.missingTeachers.slice(0, 12).map((t, i) => (
              <li key={i} className="rounded-lg bg-muted/50 px-3 py-2">
                <div className="font-medium">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.staffNo ?? "—"} · {t.email ?? "—"}</div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* AI executive report */}
      <ExecutiveReport schoolId={schoolId} />
    </div>
  );
}

function ExecutiveReport({ schoolId }: { schoolId: string }) {
  const run = useServerFn(executiveReport);
  const [audience, setAudience] = useState<"head_teacher" | "director" | "board">("head_teacher");
  const m = useMutation({ mutationFn: (a: typeof audience) => run({ data: { schoolId, audience: a } }) });

  useEffect(() => { m.reset(); /* eslint-disable-next-line */ }, [schoolId]);

  const text = m.data?.text;
  const download = () => {
    if (!text) return;
    const blob = new Blob([text], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `intelligence-report-${audience}-${new Date().toISOString().slice(0, 10)}.md`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <Card title="AI Executive Report" icon={<Sparkles className="h-4 w-4 text-primary" />}
      action={
        <div className="flex flex-wrap items-center gap-2">
          <select className="rounded-lg border bg-card px-2 py-1.5 text-sm" value={audience} onChange={(e) => setAudience(e.target.value as typeof audience)}>
            <option value="head_teacher">Head Teacher</option>
            <option value="director">Director</option>
            <option value="board">Board</option>
          </select>
          <Button size="sm" onClick={() => m.mutate(audience)} disabled={m.isPending}>
            {m.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
            Generate
          </Button>
          {text && !m.data?.error && (
            <Button size="sm" variant="outline" onClick={download}><Download className="mr-2 h-4 w-4" />Download</Button>
          )}
        </div>
      }
    >
      {!text && !m.isPending && (
        <p className="text-sm text-muted-foreground">Generate a one-page narrative report covering performance, attendance, finance, curriculum and predictions.</p>
      )}
      {m.isPending && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />MAKAI is drafting the report…</div>
      )}
      {text && (
        <div className={cn("prose prose-sm max-w-none dark:prose-invert", m.data?.error && "text-destructive")}>
          <ReactMarkdown>{text}</ReactMarkdown>
        </div>
      )}
    </Card>
  );
}

// ---- small UI ----
function Card({ title, icon, action, children }: { title: string; icon?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border/70 bg-card p-4 shadow-card sm:p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">{icon}{title}</div>
        {action}
      </div>
      {children}
    </section>
  );
}
function KpiCard({ icon, label, value, tone }: { icon: React.ReactNode; label: string; value: string; tone: "primary" | "success" | "info" | "warn" | "default" }) {
  const ring = { primary: "bg-primary-soft text-primary", success: "bg-emerald-100 text-emerald-700", info: "bg-sky-100 text-sky-700", warn: "bg-amber-100 text-amber-700", default: "bg-muted text-muted-foreground" }[tone];
  return (
    <div className="rounded-2xl border border-border/70 bg-card p-3 shadow-card">
      <div className={cn("mb-2 inline-flex h-7 w-7 items-center justify-center rounded-lg", ring)}>{icon}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate font-display text-lg font-bold">{value}</div>
    </div>
  );
}
function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-muted/50 px-3 py-2 text-center">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-display text-sm font-bold">{value}</div>
    </div>
  );
}
function Pill({ tone, children }: { tone: "success" | "warn" | "danger" | "muted"; children: React.ReactNode }) {
  const cls = { success: "bg-emerald-100 text-emerald-800", warn: "bg-amber-100 text-amber-800", danger: "bg-red-100 text-red-800", muted: "bg-muted text-muted-foreground" }[tone];
  return <span className={cn("rounded-md px-2 py-0.5 text-[11px] font-semibold", cls)}>{children}</span>;
}
